import { Component, computed, effect, inject, signal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { HttpResourceRef } from "@angular/common/http";
import * as z from "zod";

import { ThemeService } from "@shared/services/theme/theme.service";
import { PaymentMethod } from "@modules/checkout/enums/payment-method";
import { CardNumberSchema } from "@modules/checkout/schemas/card-number.schema";
import { ValidThruSchema } from "@modules/checkout/schemas/valid-thru.schema";
import { zodForm } from "@shared/utils/zod.utils";
import { PaymentRequestDtoSchema } from "@modules/checkout/schemas/payment-request-dto.schema";
import { PaymentFormComponent } from "@modules/checkout/components/payment-form/payment-form.component";
import { PaymentAttempt } from "@modules/checkout/types/payment-attempt";
import { PaymentFinishComponent } from "@modules/checkout/components/payment-finish/payment-finish.component";
import { TransactionStatus } from "@modules/checkout/enums/transaction-status";
import { PaymentStatus } from "@modules/checkout/enums/payment-status";
import { isFuture } from "@shared/utils/date.utils";
import { TransactionStore } from "@modules/checkout/stores/transaction/transaction.store";
import { TransactionService } from "@modules/checkout/services/transaction/transaction.service";
import { ThreeDSService, ThreeDSAuthResult } from "@modules/checkout/services/3ds/3ds.service";
import { BpmpiFormComponent } from "@modules/checkout/components/bpmpi-form/bpmpi-form.component";
import { ModalService } from "@app/shared/services/modal/modal.service";
import { MessageCardComponent } from "@app/shared/components/message-card/message-card.component";
import { ExceptionPreprocessSchema } from "@app/shared/schemas/exception.schema";

@Component({
  selector: "app-index-page",
  imports: [
    ReactiveFormsModule,
    PaymentFormComponent,
    PaymentFinishComponent,
    MessageCardComponent,
    BpmpiFormComponent,
  ],
  templateUrl: "./index-page.component.html",
  styleUrl: "./index-page.component.scss",
  host: {
    class: "h-full",
  },
})
export class IndexPageComponent {
  constructor() {
    effect(() => {
      if (!this.isCreditCard()) {
        this.creditCardForm.reset();
      }
    });

    effect(() => {
      const transactionId = this.transaction.transactionId();
      const transaction = this.transaction.resource.value();
      if (!transactionId || !transaction) return;
      if (transaction.PaymentRules.some((rule) => rule.Method === PaymentMethod.CreditCard)) {
        this.threeDSService.fetchToken(transactionId);
      }
    });

    effect(() => {
      if (!this.transaction.resource.hasValue()) return;

      const establishment = this.transaction.resource.value().Establishment;
      if (!establishment) return;

      this.themeService.setTheme({
        name: establishment.FullName,
        assets: {
          logo: establishment.LogoUrl,
          favicon: establishment.FaviconUrl,
          badges: establishment.Badges.map((badge, index) => ({
            src: badge.ImageUrl,
            url: badge.Url,
            name: `Badge ${index + 1}`,
          })),
        },
        colors: {
          primary: establishment.Color,
        },
      });
    });

    effect(() => {
      const payment = this.payment();

      if (!payment) return;

      const error = payment.error();
      if (error) {
        this.isSubmitting.set(false);

        const result = ExceptionPreprocessSchema.safeParse(error);
        const exception = result.success ? result.data : null;

        this.modal.open({
          title: "Não foi possível concluir a transação!",
          message:
            exception?.detail && exception?.title === "Payment Error"
              ? exception.detail
              : [
                  "Não foi possível concluir a transação com o método de pagamento escolhido.",
                  "Por favor, escolha <b>outro método de pagamento</b> ou tente novamente.",
                ],
          type: "error",
          actions: [
            {
              label: "Alterar forma de pagamento",
              icon: "right",
              onClick: () => this.modal.close(),
            },
          ],
        });
      }

      if (payment.hasValue()) {
        this.isSubmitting.set(false);

        const value = payment.value();

        this.transaction.updatePayment(value);

        if (value.Method === PaymentMethod.Boleto && value.ExtraData) {
          window.open(value.ExtraData.Link, "_blank");
        }
      }
    });
  }

  private readonly themeService = inject(ThemeService);
  protected readonly theme = this.themeService.theme;
  private readonly service = inject(TransactionService);
  protected readonly transaction = inject(TransactionStore);
  protected readonly threeDSService = inject(ThreeDSService);
  protected readonly modal = inject(ModalService);

  protected isSubmitting = signal(false);

  protected payment = signal<Nullable<HttpResourceRef<PaymentAttempt | undefined>>>(null);

  protected paymentInfo = computed<Nullable<PaymentAttempt>>(
    () => this.transaction.resource.value()?.CurrentPaymentAttempt ?? null,
  );

  protected showPixStep = computed(() => {
    const paymentInfo = this.paymentInfo();
    if (paymentInfo?.Method !== PaymentMethod.PIX || !paymentInfo.ExtraData) return false;
    if (paymentInfo.Status === PaymentStatus.Failed) return false;
    return isFuture(paymentInfo.ExtraData.ExpireAt, 3000);
  });

  protected readonly errorMessage = computed(() => {
    if (
      this.transaction.resource.hasValue() &&
      this.transaction.resource.value().Status !== TransactionStatus.Available
    ) {
      return this.transaction.resource.value().Status === TransactionStatus.Canceled
        ? "A transação solicitada foi cancelada e não está mais disponível"
        : "A transação solicitada expirou e não está mais disponível";
    }

    return "A transação solicitada não pode ser encontrada ou está indisponível.";
  });

  protected readonly hasPaymentMethod = computed(() => this.method() !== null);
  protected readonly isCreditCard = computed(() => this.method() === PaymentMethod.CreditCard);

  /** Whether the submit button should be disabled because 3DS is not ready yet. */
  protected readonly threeDSBlocking = computed(
    () =>
      this.isCreditCard() && this.threeDSService.isEnabled() && !this.threeDSService.scriptReady(),
  );

  protected paymentMethodForm = new FormGroup({
    method: new FormControl<Nullable<PaymentMethod>>(null),
  });

  protected creditCardForm = zodForm({
    cardHolder: {
      defaultValue: "",
      schema: z.string().min(1, { message: "O nome impresso no cartão é obrigatório" }),
    },
    cardNumber: {
      defaultValue: "",
      schema: CardNumberSchema,
    },
    validThru: {
      defaultValue: "",
      schema: ValidThruSchema,
    },
    cvv: {
      defaultValue: "",
      schema: z
        .string()
        .nonempty({ message: "O CVV é obrigatório" })
        .regex(/^\d{3}$/, { message: "CVV inválido" }),
    },
    installments: {
      defaultValue: null,
      schema: z.preprocess(
        (value) => (!value ? undefined : value),
        z.coerce
          .number({ message: "Seleciona a quantidade de parcelas" })
          .int({ message: "Quantidade de parcelas inválidas" })
          .min(1, { message: "Quantidade de parcelas inválidas" }),
      ),
    },
  });

  protected installments = toSignal(this.creditCardForm.controls.installments.valueChanges, {
    initialValue: this.creditCardForm.controls.installments.value,
  });

  protected method = toSignal(this.paymentMethodForm.controls.method.valueChanges, {
    initialValue: this.paymentMethodForm.controls.method.value,
  });

  onSubmit = async () => {
    if (this.isSubmitting() || this.transaction.isLoading()) return;

    const isCreditCard = this.isCreditCard();

    if (isCreditCard) {
      this.creditCardForm.markAllAsTouched();
      this.creditCardForm.updateValueAndValidity();

      if (this.creditCardForm.invalid) {
        return;
      }
    }

    this.isSubmitting.set(true);

    let externalAuthentication: ThreeDSAuthResult["ExternalAuthentication"] | undefined;
    let externalAuthenticationFailure: BpmpiAuthEvent | undefined;

    if (isCreditCard && this.threeDSService.isEnabled()) {
      const cardFingerprint = `${this.creditCardForm.controls.cardNumber.value}|${this.creditCardForm.controls.validThru.value}`;
      const result = await this.threeDSService.authenticate(cardFingerprint);
      externalAuthentication = result.ExternalAuthentication ?? undefined;
      externalAuthenticationFailure = result.ExternalAuthenticationFailure ?? undefined;
    }

    const payload = PaymentRequestDtoSchema.safeParse({
      Method: this.method(),
      CardHolder: isCreditCard ? this.creditCardForm.controls.cardHolder.value : undefined,
      CardNumber: isCreditCard ? this.creditCardForm.controls.cardNumber.value : undefined,
      ValidThru: isCreditCard ? this.creditCardForm.controls.validThru.value : undefined,
      CVV: isCreditCard ? this.creditCardForm.controls.cvv.value : undefined,
      Installments: isCreditCard ? this.creditCardForm.controls.installments.value : undefined,
      ExternalAuthentication: externalAuthentication,
      ExternalAuthenticationFailure: externalAuthenticationFailure,
    });

    if (!payload.success) {
      this.isSubmitting.set(false);
      this.modal.open({
        title: "Algo saiu errado!",
        message:
          "Não foi possível concluir a transação, revise os dados inseridos e tente novamente.",
        type: "error",
        actions: [
          {
            label: "Fechar",
            variant: "secondary",
            onClick: () => this.modal.close(),
          },
        ],
      });
      return;
    }

    const payment = this.service.pay(this.transaction.transactionId, payload.data);

    this.payment.set(payment);
  };
}
